import { GasWarningCenterConfiguration } from "@domain/project/configurations/gas-warning-center-configuration";
import { Floorplan } from "@domain/project/floorplan/floorplan";
import { FloorplanEventType } from "@domain/project/floorplan/floorplan-event";
import { FloorplanGasWarningCenterCabinet } from "@domain/project/floorplan/floorplan-gas-warning-center-cabinet";
import { FloorplanItem } from "@domain/project/floorplan/floorplan-item";
import { FloorplanProductItem } from "@domain/project/floorplan/floorplan-product-item";
import { Exclude, Expose, Type } from "class-transformer";
import { IsOptional, IsString } from "class-validator";
import { v4 as uuidv4 } from "uuid";

export class FloorplanGasWarningCenter extends FloorplanProductItem<GasWarningCenterConfiguration> {
  @Exclude()
  private readonly _gasWarningCenter: GasWarningCenterConfiguration;

  @IsString()
  @Expose({ name: "gasWarningCenterId" })
  private readonly _gasWarningCenterId: string;

  @Type(() => FloorplanGasWarningCenterCabinet)
  @Expose({ name: "cabinet" })
  @IsOptional()
  private _cabinet?: FloorplanGasWarningCenterCabinet;

  constructor(
    floorplan: Floorplan,
    gasWarningCenter: GasWarningCenterConfiguration,
    id: string,
    x: number,
    y: number,
    size: number,
    cabinet?: FloorplanGasWarningCenterCabinet,
    notes?: string,
  ) {
    super(floorplan, gasWarningCenter, id, x, y, size, notes);
    this._gasWarningCenterId = gasWarningCenter?.id;
    this._gasWarningCenter = gasWarningCenter;
    this._cabinet = cabinet;
  }

  get cabinet(): FloorplanGasWarningCenterCabinet | undefined {
    return this._cabinet;
  }

  set cabinet(value: FloorplanGasWarningCenterCabinet) {
    this._cabinet = value;
  }

  setItemTextVisibility() {
    this.itemTextVisible = this._floorplan.floorplanState.gasWarningCentersItemTextVisible;
  }

  protected override createRawDuplicate(): ((x: number, y: number) => FloorplanGasWarningCenter) | undefined {
    const cabinet = this.cabinet?.copy();
    const config = this.config;
    const size = this.size;

    return (x: number, y: number) => FloorplanGasWarningCenter.create(this._floorplan, config, x, y, size, cabinet?.copy());
  }

  override delete() {
    this._floorplan.deleteGasWarningCenter(this);
  }

  override getDuplicateTargetCollection() {
    return this._floorplan.gasWarningCenters;
  }

  protected override registerDuplicate() {
    return (duplicate: FloorplanItem, afterDuplicateAdded?: (duplicate: FloorplanItem) => void) =>
      this.registerDuplicateInFloorplan(
        duplicate as FloorplanGasWarningCenter,
        this._floorplan.gasWarningCenters,
        FloorplanEventType.GAS_WARNING_CENTER_ADDED,
        () => this._floorplan.project.setPositionNumbers(),
        afterDuplicateAdded as ((duplicate: FloorplanGasWarningCenter) => void) | undefined,
      );
  }

  protected override refreshFloorplanLockState() {
    this._floorplan.floorplanState.refreshGasWarningCentersLock();
  }

  static create(
    floorplan: Floorplan,
    gasWarningCenter: GasWarningCenterConfiguration,
    x: number,
    y: number,
    size: number,
    cabinet?: FloorplanGasWarningCenterCabinet,
  ): FloorplanGasWarningCenter {
    return new FloorplanGasWarningCenter(floorplan, gasWarningCenter, uuidv4(), x, y, size, cabinet);
  }

  protected override getConfigFromProject(floorplan: Floorplan): GasWarningCenterConfiguration | undefined {
    return floorplan.getGasWarningCenterConfig(this._gasWarningCenterId);
  }
}
